import { Platform } from "react-native";
import messaging from "@react-native-firebase/messaging";

import { UserDeviceDto } from "../models/UserDeviceModel";
import { ResponseModel } from "../models/ResponseModel";
import { AjaxService } from "../shared/services/AjaxService";
import { UserRegistrationService } from "./UserRegistrationService";
import { AuthStorage } from "./storage/AuthStorage";
import { Storage } from "./storage/storage";

const DEVICE_UUID_KEY = "user_device_uuid";
const PLATFORM_ID_KEY = `user_device_platform_id_${Platform.OS}`;
const FCM_TOKEN_KEY = "fcm_token";

type EnumerationItem = {
  enumerationId?: string;
  id?: string;
  value?: string;
  name?: string;
  code?: string;
  displayName?: string;
};

type IosPlatformConstants = {
  osVersion?: string;
  interfaceIdiom?: string;
  systemName?: string;
};

let tokenRefreshUnsubscribe: (() => void) | null = null;
let foregroundMessageUnsubscribe: (() => void) | null = null;

const generateUuid = () =>
  "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (char) => {
    const random = Math.floor(Math.random() * 16);
    const value = char === "x" ? random : [8, 9, 10, 11][Math.floor(random / 4)];
    return value.toString(16);
  });

const normalizeUuid = (value?: string | null) => {
  const normalized = value?.trim();
  return normalized ? normalized : null;
};

const getStoredUuid = async () => {
  const sessionUuid = normalizeUuid((await AuthStorage.getUser())?.uuid);
  if (sessionUuid) {
    return sessionUuid;
  }

  const deviceUuid = normalizeUuid(await Storage.get(DEVICE_UUID_KEY));
  return deviceUuid;
};

const getStoredOrGeneratedUuid = async () => {
  const existingUuid = await getStoredUuid();
  if (existingUuid) {
    await Storage.set(DEVICE_UUID_KEY, existingUuid);
    return existingUuid;
  }

  const nextUuid = generateUuid();
  await Storage.set(DEVICE_UUID_KEY, nextUuid);
  return nextUuid;
};

const requestNotificationPermission = async (): Promise<boolean> => {
  try {
    if (Platform.OS === "web") {
      const authStatus = await messaging().requestPermission();
      return (
        authStatus === messaging.AuthorizationStatus.AUTHORIZED ||
        authStatus === messaging.AuthorizationStatus.PROVISIONAL
      );
    }

    if (Platform.OS === "android") {
      if (Platform.Version >= 33) {
        const { PermissionsAndroid } = require("react-native");
        const result = await PermissionsAndroid.request(
          PermissionsAndroid.PERMISSIONS.POST_NOTIFICATIONS
        );

        return result === PermissionsAndroid.RESULTS.GRANTED;
      }

      return true;
    }

    const authStatus = await messaging().requestPermission();

    return (
      authStatus === messaging.AuthorizationStatus.AUTHORIZED ||
      authStatus === messaging.AuthorizationStatus.PROVISIONAL
    );
  } catch (error) {
    console.log("FCM permission request failed:", error);
    return false;
  }
};

const getFcmToken = async (): Promise<string | null> => {
  try {
    const cachedToken = await Storage.get(FCM_TOKEN_KEY);
    if (cachedToken?.trim()) {
      console.log("Using cached FCM token:", cachedToken);
      return cachedToken.trim();
    }

    const hasPermission = await requestNotificationPermission();
    console.log("FCM permission granted:", hasPermission);

    if (!hasPermission) {
      console.log("FCM notification permission not granted");
      return null;
    }

    await messaging().registerDeviceForRemoteMessages();

    const freshToken = await messaging().getToken();
    console.log("Fresh FCM token:", freshToken);

    if (freshToken?.trim()) {
      const normalizedToken = freshToken.trim();
      await Storage.set(FCM_TOKEN_KEY, normalizedToken);
      return normalizedToken;
    }

    return null;
  } catch (error) {
    console.log("Error while getting FCM token:", error);
    return null;
  }
};

const getPlatformId = async () => {
  const cachedPlatformId = await Storage.get(PLATFORM_ID_KEY);
  if (cachedPlatformId) return cachedPlatformId;

  const enumerationResponse =
    await UserRegistrationService.getEnumeration("AppPlatform");

  const platformItems: EnumerationItem[] = Array.isArray(enumerationResponse?.data)
    ? enumerationResponse.data
    : [];

  const currentPlatform = Platform.OS.toLowerCase();

  const platformEntry = platformItems.find((item) => {
    const rawValue = String(
      item?.value ??
        item?.name ??
        item?.code ??
        item?.displayName ??
        ""
    )
      .trim()
      .toLowerCase();

    return (
      rawValue === currentPlatform ||
      (currentPlatform === "ios" && ["iphone", "ipad", "ios"].includes(rawValue)) ||
      (currentPlatform === "android" && ["android", "googleplay"].includes(rawValue)) ||
      (currentPlatform === "web" && ["web", "browser", "website", "desktopweb"].includes(rawValue))
    );
  });

  const resolvedPlatformId = String(
    platformEntry?.enumerationId ?? platformEntry?.id ?? ""
  ).trim();

  if (resolvedPlatformId) {
    await Storage.set(PLATFORM_ID_KEY, resolvedPlatformId);
  }

  return resolvedPlatformId || null;
};

const getDeviceDetails = () => {
  if (Platform.OS === "web") {
    const browserNavigator = typeof globalThis !== "undefined"
      ? (globalThis as any).navigator
      : undefined;
    const userAgent = browserNavigator?.userAgent ?? "";

    return {
      osversion: userAgent || "web",
      brand: browserNavigator?.platform || "Browser",
      model: browserNavigator?.userAgentData?.brands?.[0]?.brand ?? "Web Browser",
    };
  }

  if (Platform.OS === "android") {
    return {
      osversion: String(Platform.constants.Release ?? Platform.Version ?? ""),
      brand: String(
        Platform.constants.Brand ??
          Platform.constants.Manufacturer ??
          "Android"
      ),
      model: String(Platform.constants.Model ?? ""),
    };
  }

  const iosConstants = Platform.constants as IosPlatformConstants;

  return {
    osversion: String(iosConstants.osVersion ?? Platform.Version ?? ""),
    brand: iosConstants.systemName?.trim() || "Apple",
    model: iosConstants.interfaceIdiom?.trim() || null,
  };
};

export const UserDeviceService = {
  async initFcm() {
    const token = await getFcmToken();
    console.log("Initial FCM token:", token);

    if (!tokenRefreshUnsubscribe) {
      tokenRefreshUnsubscribe = messaging().onTokenRefresh(async (nextToken) => {
        try {
          if (nextToken?.trim()) {
            const normalizedToken = nextToken.trim();
            await Storage.set(FCM_TOKEN_KEY, normalizedToken);
            console.log("FCM token refreshed and saved:", normalizedToken);
          }
        } catch (error) {
          console.log("Error saving refreshed FCM token:", error);
        }
      });
    }

    if (Platform.OS === "web" && !foregroundMessageUnsubscribe) {
      foregroundMessageUnsubscribe = messaging().onMessage(async (message: any) => {
        try {
          const browserNavigator = typeof globalThis !== "undefined"
            ? (globalThis as any).navigator
            : undefined;
          const browserNotification = typeof globalThis !== "undefined"
            ? (globalThis as any).Notification
            : undefined;

          if (!browserNotification || browserNotification.permission !== "granted") {
            return;
          }

          const title = String(
            message?.notification?.title ??
              message?.data?.title ??
              "Gaur Prem Aashray"
          ).trim();
          const body = String(
            message?.notification?.body ??
              message?.data?.body ??
              ""
          ).trim();
          const icon = String(message?.data?.icon ?? "/favicon.ico").trim() || "/favicon.ico";

          const registration = await browserNavigator?.serviceWorker?.getRegistration?.();
          if (registration?.showNotification) {
            await registration.showNotification(title, {
              body,
              icon,
              data: message,
            });
            return;
          }

          new browserNotification(title, {
            body,
            icon,
          });
        } catch (error) {
          console.log("Web foreground notification display failed:", error);
        }
      });
    }

    return token;
  },

  async getStoredFcmToken() {
    const token = await Storage.get(FCM_TOKEN_KEY);
    return token?.trim() ? token.trim() : null;
  },

  async clearStoredFcmToken() {
    await Storage.remove(FCM_TOKEN_KEY);
  },

  async setCurrentUuid(uuid: string) {
    const normalizedUuid = normalizeUuid(uuid);
    if (!normalizedUuid) {
      throw new Error("UUID is required.");
    }

    await Storage.set(DEVICE_UUID_KEY, normalizedUuid);
    return normalizedUuid;
  },

  async getCurrentUuid() {
    return getStoredOrGeneratedUuid();
  },

  async buildCurrentDevicePayload(userId: string): Promise<UserDeviceDto> {
    const [uuid, platformId, fcmToken] = await Promise.all([
      this.getCurrentUuid(),
      getPlatformId(),
      getFcmToken(),
    ]);

    if (!platformId) {
      throw new Error(
        Platform.OS === "web"
          ? "Web platform configuration was not found. Add a Web AppPlatform entry in the backend enumeration."
          : "Platform details could not be resolved for this device."
      );
    }

    const deviceDetails = getDeviceDetails();

    return {
      UserDeviceId: generateUuid(),
      UserId: userId,
      Uuid: uuid,
      PlatformId: platformId,
      FcmToken: fcmToken,
      Osversion: deviceDetails.osversion || null,
      Brand: deviceDetails.brand || null,
      Model: deviceDetails.model || null,
    };
  },

  async createUserDevice(payload: UserDeviceDto): Promise<ResponseModel> {
    return AjaxService.post<ResponseModel>("/User/create-user-device", payload);
  },

  async deleteUserDevice(): Promise<ResponseModel> {
    return AjaxService.delete<ResponseModel>(
      `/User/delete-user-device`
    );
  },

  async registerCurrentDevice(userId?: string | null): Promise<ResponseModel | null> {
    const normalizedUserId = userId?.trim();
    if (!normalizedUserId) return null;

    const payload = await this.buildCurrentDevicePayload(normalizedUserId);
    console.log("Registering device payload:", payload);

    return this.createUserDevice(payload);
  },

  async deleteCurrentDevice(): Promise<ResponseModel | null> {

    const response = await this.deleteUserDevice();

    try {
      await messaging().deleteToken();
    } catch (error) {
      console.log("Error deleting FCM token:", error);
    }

    await this.clearStoredFcmToken();
    return response;
  },
};
